import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';

interface SeatInfo {
  id: string;
  seatNumber: number;
  status: string;
}

interface TableInfo {
  id: string;
  tableNumber: number;
  tableName: string;
  zoneName: string;
  zoneColor: string;
  seats: SeatInfo[];
}

interface ZoneGroup {
  name: string;
  color: string;
  tables: TableInfo[];
  totalSeats: number;
  occupied: number;
  available: number;
}

const SEAT_COLORS: Record<string, string> = {
  Available: '#4caf50',
  Assigned: '#1565c0',
  CheckedIn: '#7b1fa2',
  Held: '#f9a825',
  Blocked: '#757575',
};

export function MobileVisualTableMap() {
  const [zones, setZones] = useState<ZoneGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'available' | 'full'>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const tables = await apiClient<Array<{
        id: string;
        tableNumber: number;
        tableName: string;
        zone: { name: string; colorCode: string };
        seats: Array<{ id: string; seatNumber: number; status: string }>;
      }>>('/table-management/tables?include=seats');

      const zoneMap = new Map<string, { color: string; tables: TableInfo[] }>();
      for (const t of tables) {
        if (!zoneMap.has(t.zone.name)) zoneMap.set(t.zone.name, { color: t.zone.colorCode, tables: [] });
        zoneMap.get(t.zone.name)!.tables.push({
          id: t.id,
          tableNumber: t.tableNumber,
          tableName: t.tableName,
          zoneName: t.zone.name,
          zoneColor: t.zone.colorCode,
          seats: (t.seats || []).map((s) => ({ id: s.id, seatNumber: s.seatNumber, status: s.status })),
        });
      }

      const groups: ZoneGroup[] = Array.from(zoneMap.entries())
        .map(([name, data]) => {
          const totalSeats = data.tables.reduce((s, t) => s + t.seats.length, 0);
          const occupied = data.tables.reduce((s, t) => s + t.seats.filter((seat) => seat.status === 'Assigned' || seat.status === 'CheckedIn').length, 0);
          return { name, color: data.color, tables: data.tables.sort((a, b) => a.tableNumber - b.tableNumber), totalSeats, occupied, available: totalSeats - occupied };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setZones(groups);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div style={{ padding: '16px', color: '#666', fontSize: '0.85rem', textAlign: 'center' }}>Loading table map...</div>;

  return (
    <div>
      {/* Filter pills */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {([['all', 'All'], ['available', 'Has Seats'], ['full', 'Full']] as const).map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} style={{
            padding: '5px 12px', borderRadius: '16px', fontSize: '0.75rem', fontWeight: filter === key ? 700 : 500,
            border: filter === key ? '2px solid #1a6b3c' : '1px solid #d1d5db',
            background: filter === key ? '#e8f5e9' : '#fff', color: filter === key ? '#1a6b3c' : '#666', cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {Object.entries(SEAT_COLORS).map(([s, c]) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', color: '#666' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: c }} />{s}
          </div>
        ))}
      </div>

      {/* Zones */}
      {zones.map((zone) => {
        const filteredTables = zone.tables.filter((t) => {
          if (filter === 'all') return true;
          const avail = t.seats.filter((s) => s.status === 'Available').length;
          return filter === 'available' ? avail > 0 : avail === 0;
        });
        if (filteredTables.length === 0 && filter !== 'all') return null;
        const pct = zone.totalSeats > 0 ? Math.round((zone.occupied / zone.totalSeats) * 100) : 0;

        return (
          <div key={zone.name} style={{ marginBottom: '20px' }}>
            {/* Zone header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingBottom: '6px', borderBottom: `2px solid ${zone.color}` }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: zone.color }} />
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f2d1f' }}>{zone.name}</span>
              <span style={{ fontSize: '0.7rem', color: '#666' }}>{pct}% &bull; {zone.available} avail</span>
            </div>

            {/* Table grid — 2 columns for mobile */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {(filter === 'all' ? zone.tables : filteredTables).map((table) => {
                const occupied = table.seats.filter((s) => s.status === 'Assigned' || s.status === 'CheckedIn').length;
                const available = table.seats.filter((s) => s.status === 'Available').length;
                const total = table.seats.length;
                const tablePct = total > 0 ? Math.round((occupied / total) * 100) : 0;
                const isFiltered = filter !== 'all' && !filteredTables.includes(table);

                if (isFiltered) {
                  return (
                    <div key={table.id} style={{ borderRadius: '10px', border: '2px dashed #ccc', opacity: 0.4, padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px' }}>
                      <span style={{ fontSize: '0.7rem', color: '#999' }}>{table.tableName}</span>
                    </div>
                  );
                }

                // Circular table mini visualization
                const radius = 28;
                const cx = 38, cy = 38, seatR = 5;
                return (
                  <div key={table.id} style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                    {/* Header */}
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f2d1f', marginBottom: '4px' }}>{table.tableName}</div>

                    {/* SVG mini table */}
                    <svg width="76" height="76" viewBox="0 0 76 76" style={{ display: 'block', margin: '0 auto' }}>
                      <circle cx={cx} cy={cy} r={20} fill="#f5f7f6" stroke="#e5e7eb" strokeWidth="1.5" />
                      <circle cx={cx} cy={cy} r={20} fill="none" stroke={tablePct === 100 ? '#7b1fa2' : '#1a6b3c'} strokeWidth="2"
                        strokeDasharray={`${(tablePct / 100) * 125.6} 125.6`} strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`} opacity={0.7} />
                      <text x={cx} y={cy + 3} textAnchor="middle" fontSize="10" fontWeight="800" fill="#0f2d1f">{occupied}/{total}</text>
                      {table.seats.sort((a, b) => a.seatNumber - b.seatNumber).map((seat, idx) => {
                        const angle = (idx / total) * 2 * Math.PI - Math.PI / 2;
                        return <circle key={seat.id} cx={cx + radius * Math.cos(angle)} cy={cy + radius * Math.sin(angle)} r={seatR} fill={SEAT_COLORS[seat.status] || '#999'} opacity={0.8} />;
                      })}
                    </svg>

                    {/* Pills */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '0.68rem', fontWeight: 700 }}>{available}</span>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#e3f2fd', color: '#1565c0', fontSize: '0.68rem', fontWeight: 700 }}>{occupied}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
